import {
  Component,
  effect,
  inject,
  OnInit,
  output,
  OutputEmitterRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { SectionComponent } from '../../../components/layout/section/section.component';
import { SectionHeaderComponent } from '../../../components/layout/section/section-header/section-header.component';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { DEFAULT_CUSTOM_TILE_SECTION, ERelationType } from '@web/dtos';
import { StartPageSectionBlockComponent } from './start-page-section-block/start-page-section-block.component';
import { CustomTileSectionFormsFactoryService } from '../../../factory/customTileSection/custom-tile-section-forms-factory';
import { CustomTileSectionStore } from '../../../services/api/custom-tile-section/custom-tile-section-store.service';
import { ICustomTileSectionForm } from '../../../entities/custom-tile-section.entity';
import { SectionHeaderActionsComponent } from '../../../components/layout/section/section-header/section-header-actions/section-header-actions.component';
import { SectionHeadlineComponent } from '../../../components/layout/section/section-header/section-headline/section-headline.component';
import { ICustomTileForm } from '../../../entities/custom-tile.entity';
import { FormTracking } from '../form-tracking/form-tracking.component';
import { LanguageSwitcherComponent } from '../../../components/data-input/form-inputs/language-switcher/language-switcher.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';

@Component({
  selector: 'custom-tile-page',
  templateUrl: './start-page.component.html',
  imports: [
    StartPageSectionBlockComponent,
    SectionComponent,
    SectionHeaderComponent,
    ButtonComponent,
    ReactiveFormsModule,
    SectionHeaderActionsComponent,
    SectionHeadlineComponent,
    LanguageSwitcherComponent,
    LoadingAnimationComponent,
  ],
})
export class StartPageComponent
  extends FormTracking<ICustomTileSectionForm>
  implements OnInit
{
  public readonly atDelete: OutputEmitterRef<
    FormArray<FormGroup<ICustomTileForm>>
  > = output<FormArray<FormGroup<ICustomTileForm>>>();
  protected readonly customTileSectionFactory: CustomTileSectionFormsFactoryService =
    inject(CustomTileSectionFormsFactoryService);
  protected readonly customTileSectionStore: CustomTileSectionStore = inject(
    CustomTileSectionStore
  );
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  public customTileSectionForms!: FormArray<FormGroup<ICustomTileSectionForm>>;
  protected readonly language: FormControl<string> =
    this.formBuilder.nonNullable.control<string>('');
  protected readonly TextRelationType: typeof ERelationType = ERelationType;
  protected readonly sectionsToBeDeleted: WritableSignal<string[]> = signal([]);
  protected readonly isLoading: Signal<boolean> =
    this.customTileSectionStore.isLoading;

  constructor() {
    super();
    this.customTileSectionStore.reload();
    effect(() => {
      this.customTileSectionForms =
        this.customTileSectionFactory.buildSectionsFormArray(
          this.customTileSectionStore.customTileSections()
        );
      this.initFormTracking(this.customTileSectionForms);
      this.customTileSectionForms.updateValueAndValidity();
    });
  }

  ngOnInit(): void {
    window.onbeforeunload = (event: BeforeUnloadEvent) => {
      if (this.customTileSectionForms.dirty) {
        event.preventDefault();
      }
    };
  }

  public resetFormChanges(): void {
    this.customTileSectionStore.reload();
    this.sectionsToBeDeleted.set([]);
  }

  protected deleteSection(index: number, sectionId: string | null): void {
    if (sectionId) {
      this.sectionsToBeDeleted.set(
        this.sectionsToBeDeleted().concat(sectionId)
      );
    }
    this.customTileSectionForms.removeAt(index);
    this.customTileSectionForms.markAsDirty();
  }

  protected addNewSection(): void {
    this.customTileSectionForms.controls.push(
      this.customTileSectionFactory.buildForm({
        ...DEFAULT_CUSTOM_TILE_SECTION,
        position: this.customTileSectionForms.length,
        tiles: [],
      })
    );
    this.customTileSectionForms.markAsDirty();
  }

  protected save(): boolean {
    if (!this.customTileSectionForms.valid) {
      this.customTileSectionForms.markAllAsTouched();
      return false;
    }
    this.sectionsToBeDeleted().forEach(section => {
      this.customTileSectionStore.delete(section);
    });
    this.sectionsToBeDeleted.set([]);
    this.customTileSectionStore.save(
      this.customTileSectionFactory.buildEntities(this.customTileSectionForms)
    );
    return true;
  }

  protected moveItemAtIndex(index: number, newIndex: number): void {
    const currentGroup: FormGroup<ICustomTileSectionForm> =
      this.customTileSectionForms.at(index);
    this.customTileSectionForms.removeAt(index);
    this.customTileSectionForms.insert(newIndex, currentGroup);
    this.customTileSectionForms.updateValueAndValidity();
    this.customTileSectionForms.controls.forEach(
      (customTileSection: FormGroup<ICustomTileSectionForm>, index: number) => {
        customTileSection.controls.position.setValue(index);
      }
    );

    this.customTileSectionForms.markAsDirty();
  }
}
